import dayjs from 'dayjs';
import TextConstants from 'src/common/helpers/TextConstants';
import { parseJwt } from '.';
import ApiConstants from './ApiConstants';
import {
  getUserRefreshToken,
  getUserToken,
  setUserToken,
} from './StorageUtils';

export const getRequest = (url, params, authenticatedRequest) => {
  return httpRequest('get', url, params, authenticatedRequest);
};

export const putRequest = (url, params, authenticatedRequest) => {
  return httpRequest('put', url, params, authenticatedRequest);
};

export const patchRequest = (url, params, authenticatedRequest) => {
  return httpRequest('PATCH', url, params, authenticatedRequest);
};

export const postRequest = (url, params, authenticatedRequest) => {
  return httpRequest('post', url, params, authenticatedRequest);
};

export const deleteRequest = (url, params, authenticatedRequest) => {
  return httpRequest('delete', url, params, authenticatedRequest);
};

let _is_refreshing_token = false;

const refreshAccessToken = async (refreshToken) => {
  try {
    _is_refreshing_token = true;

    const _refresh_access_token_url = `${ApiConstants.BASE_URL}${ApiConstants.REFRESH_ACCESS_TOKEN_URL}`;
    const response = await fetch(_refresh_access_token_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken }),
    });

    if (response.ok) {
      const data = await response.json();
      const newAccessToken = data['idToken'];
      setUserToken(newAccessToken);
      _is_refreshing_token = false;
      return newAccessToken;
    } else {
      console.log(
        '1. Error when refreshing token! Status code:',
        response.status
      );
      // TODO: logout here?
      throw new Error('Error when refreshing token!');
    }
  } catch (error) {
    console.log('2. Error when refreshing token!', error);
    // TODO: force logout here
    // logoutUser();
    // store.dispatch(logout());
  }
};

const isTokenHasExpired = (token) => {
  const parseToken = parseJwt(token);
  const expTime = dayjs(parseToken.exp * 1000);
  const currTime = dayjs();
  return !currTime.isBefore(expTime);
};

const httpRequest = async (
  method,
  url,
  params,
  authenticatedRequest = true
) => {
  if (!navigator.onLine) {
    let message = '';
    if (!window.__shownDisconnectedMessage) {
      message = TextConstants.Common.ScreenUpdateCommunicationFailed;
      window.__shownDisconnectedMessage = true;
    }
    return Promise.reject({ message });
  } else {
    delete window.__shownDisconnectedMessage;
  }

  let token;
  if (authenticatedRequest) {
    token = getUserToken();

    while (_is_refreshing_token) {
      await new Promise((resolve) => setTimeout(resolve, 500));
      // get new access token
      token = getUserToken();
    }

    if (!token) {
      return Promise.reject({
        message: TextConstants.Common.SessionExpired,
      });
    }

    if (isTokenHasExpired(token)) {
      const refreshToken = getUserRefreshToken();
      if (!refreshToken) {
        return Promise.reject({
          message: TextConstants.Common.SessionExpired,
        });
      }

      try {
        token = await refreshAccessToken(refreshToken);
      } catch (_) {
        return Promise.reject({
          message: TextConstants.Common.SessionExpired,
        });
      }
    }
  }

  const options = {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(token && { Authorization: 'Bearer ' + token }),
    },
    method,
    ...(params &&
      method.toLowerCase() !== 'get' && { body: JSON.stringify(params) }),
  };

  return new Promise((resolve, reject) => {
    fetch(url, options)
      .then((response) => {
        return new Promise((resolve) =>
          response.text().then((resText) => {
            resolve({
              status: response.status,
              ok: response.ok,
              json: resText === '' || !response.ok ? {} : JSON.parse(resText),
              message: resText === '' ? '' : JSON.parse(resText)?.message,
              data: resText === '' ? '' : JSON.parse(resText)?.data,
            });
          })
        );
      })
      .then((response) => {
        if (response.ok) {
          return resolve(response.json);
        } else if (
          response.status === 401 &&
          ['Unauthorized', 'The incoming token has expired'].includes(
            response.message
          )
        ) {
          return reject({
            ...response,
            message: TextConstants.Common.SessionExpired,
          });
        } else if (response.status === 403) {
          // TODO : perform authorization or redirected to login
          return reject(response);
        } else {
          return reject(response);
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
};
